import {
  Language,
  _Boolean,
  ModelStringKeyConditionInput,
  AnnouncementSetting,
  Product,
} from '@/API';
import { LinkMenuType } from '@/appsync/graphql/link';
import {
  graphqlQuery,
  getDescriptionByLang,
  productAdditionalQueryMap,
} from '@/appsync/service/GraphqlUtils';
import { matchCidrs } from '@/appsync/service/MenuQueryService';
import { commonModule } from '@/store/modules/CommonStore';
import {
  GetTenantLandscapeAllInfoQueryVariables,
  GetTenantLandscapeAllInfoQuery,
} from '@/types/type';
import {
  LinkMenu,
  TenantLandscape,
  TenantLandscapeAllInfo,
  GetGatewayServerType,
} from '@/types/type';
import { _BooleanEnum2boolean } from '@/utils/CommonUtils';

export const LinkMenuLanguage = Language.en;

export const fetchTenantLandscapeAllInfo = async (
  param: TenantLandscape
): Promise<TenantLandscapeAllInfo> => {
  const landscapeKeyConditionInput: ModelStringKeyConditionInput = {
    eq: param.landscape,
  };
  const variables: GetTenantLandscapeAllInfoQueryVariables = {
    tenant: param.tenant,
    landscape: param.landscape,
    landscapeKeyConditionInput,
    linkMenusLimit: 100,
  };
  if (commonModule.cognitoInfo) {
    const identityProvider = commonModule.cognitoInfo.identityProvider;
    variables.identityProvider = identityProvider;
  }

  const result = await graphqlQuery<
    GetTenantLandscapeAllInfoQuery,
    GetTenantLandscapeAllInfoQueryVariables
  >(getTenantLandscapeAllInfo(!!commonModule.cognitoInfo), variables);

  return {
    gatewayServerCWS: result ? result.gatewayServerCWS : gatewayServer,
    gatewayServerCSR: result ? result.gatewayServerCSR : gatewayServer,
    linkMenus: linkMenus(result),
    productAdditionalQueryMap: productAdditionalQueryMap(result),
    announcementSetting: announcementSetting(result),
  };
};

export const gatewayServer: GetGatewayServerType = {
  useInternetAccess: _Boolean._false,
  isInternalGateWayServer: _Boolean._false,
  gatewayServerIps: [''],
};

export const isInHouseIP = (
  userIps: string[] | null,
  gatewayServer: GetGatewayServerType
): boolean => {
  if (gatewayServer && _BooleanEnum2boolean(gatewayServer.useInternetAccess)) {
    const includesResult: boolean = matchCidrs(
      gatewayServer.gatewayServerIps,
      userIps
    );
    return _BooleanEnum2boolean(gatewayServer.isInternalGateWayServer)
      ? includesResult
      : !includesResult;
  }
  return true;
};

const linkMenus = (
  result: GetTenantLandscapeAllInfoQuery | null
): LinkMenu[] => {
  return result && result.linkMenus && result.linkMenus.items
    ? result.linkMenus.items
        .sort((menu1, menu2) => menu1.sort - menu2.sort)
        .map(
          (menu: LinkMenuType): LinkMenu => {
            const title = menu.menuDescriptions
              ? getDescriptionByLang(
                  menu.menuDescriptions.items,
                  LinkMenuLanguage
                )
              : '';
            return {
              id: menu.id,
              title,
              menuType: 'link',
              url: menu.url,
              icon: 'mdi-open-in-new',
            };
          }
        )
    : [];
};

const announcementSetting = (
  result: GetTenantLandscapeAllInfoQuery | null
): AnnouncementSetting => {
  return result &&
    result.systemSettingInfo &&
    result.systemSettingInfo.announcementSetting
    ? result.systemSettingInfo.announcementSetting
    : AnnouncementSetting.UseV1;
};

const authQueryV8Product = (productName: Product) => `
  authQuery${productName}: getAuthQuery(
    tenant: $tenant
    landscape: $landscape
    identityProvider: $identityProvider
    product: ${productName}
  ) {
    product
    additionalQuery
  }
`;

const authQueryCJK = authQueryV8Product(Product.CJK);
const authQueryCWS = authQueryV8Product(Product.CWS);
const authQueryCSR = authQueryV8Product(Product.CSR);
const authQueryCTM = authQueryV8Product(Product.CTM);

const gatewayServerV8Product = (productName: Product) => `
  gatewayServer${productName}: getGatewayServer(
    tenant: $tenant
    landscape: $landscape
    product: ${productName}
  ) {
    useInternetAccess
    isInternalGateWayServer
    gatewayServerIps
  }
`;

const gatewayServerCWS = gatewayServerV8Product(Product.CWS);
const gatewayServerCSR = gatewayServerV8Product(Product.CSR);

const linkMenusQuery = `
  linkMenus: linkByTenantLandscape(
    tenant: $tenant
    landscape: $landscapeKeyConditionInput
    limit: $linkMenusLimit
    nextToken: $nextToken
  ) {
    items {
      id
      menuDescriptions {
        items {
          language
          description
        }
        nextToken
      }
      url
      sort
    }
    nextToken
  }
`;
const systemSettingInfo = `
  systemSettingInfo: getSystemSettingInfo(
    tenant: $tenant
    landscape: $landscape
  ) {
    announcementSetting
  }
`;
export const getTenantLandscapeAllInfo = (
  cognitoInfoExists: boolean
) => /* GraphQL */ {
  if (cognitoInfoExists) {
    return `
      query GetTenantLandscapeAllInfo(
          $tenant: String!
          $landscape: String!
          $identityProvider: String!
          $landscapeKeyConditionInput: ModelStringKeyConditionInput
          $linkMenusLimit: Int
          $nextToken: String
      ) {
          ${authQueryCJK}
          ${authQueryCWS}
          ${authQueryCSR}
          ${authQueryCTM}
          ${gatewayServerCWS}
          ${gatewayServerCSR}
          ${linkMenusQuery}
          ${systemSettingInfo}
      }
      `;
  }
  return `
      query GetTenantLandscapeAllInfo(
          $tenant: String!
          $landscape: String!
          $landscapeKeyConditionInput: ModelStringKeyConditionInput!
          $linkMenusLimit: Int!
          $nextToken: String
      ) {
          ${gatewayServerCWS}
          ${gatewayServerCSR}
          ${linkMenusQuery}
          ${systemSettingInfo}
      }
      `;
};
export const getProductAuthQuery = /* GraphQL */ `
  query GetProductAuthQuery(
      $tenant: String!
      $landscape: String!
      $identityProvider: String!
  ) {
      ${authQueryCJK}
      ${authQueryCWS}
      ${authQueryCSR}
      ${authQueryCTM}
  }
`;
